import axios from 'axios';

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
const API = `${BACKEND_URL}/api/admin`;
const ADMIN_TOKEN_KEY = 'adminToken';
const ADMIN_SESSION_KEY = 'adminSession';
let cachedAdminSession = null;
const adminClient = axios.create({
  baseURL: API,
  timeout: 15000,
  withCredentials: true,
});

// Compatibility only: lets existing pre-cookie sessions keep working briefly.
const getAuthHeader = () => {
  const token = localStorage.getItem(ADMIN_TOKEN_KEY);
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const getSiteHeader = (siteId) => (siteId ? { 'X-Site-Id': siteId } : {});

const buildHeaders = (siteId = null) => ({
  ...getAuthHeader(),
  ...getSiteHeader(siteId),
});

adminClient.interceptors.request.use((config) => {
  config.headers = {
    ...(config.headers || {}),
    ...getAuthHeader(),
  };
  return config;
});

adminClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error?.response?.status === 401) {
      cachedAdminSession = null;
      localStorage.removeItem(ADMIN_TOKEN_KEY);
      localStorage.removeItem(ADMIN_SESSION_KEY);
      if (typeof window !== 'undefined' && !window.location.pathname.includes('/admin/login')) {
        window.location.replace('/admin/login');
      }
    }
    return Promise.reject(error);
  }
);

// Auth
export const adminLogin = async (username, password) => {
  try {
    const response = await adminClient.post('/login', { username, password });
    if (response.data.user) {
      cachedAdminSession = response.data.user;
    }
    localStorage.removeItem(ADMIN_TOKEN_KEY);
    localStorage.removeItem(ADMIN_SESSION_KEY);
    return response.data;
  } catch (error) {
    console.error('Login error:', error);
    throw error;
  }
};

export const adminLogout = async () => {
  try {
    await adminClient.post('/logout');
  } catch (error) {
    // Local cleanup should still happen even if the API is unreachable.
  }
  cachedAdminSession = null;
  localStorage.removeItem(ADMIN_TOKEN_KEY);
  localStorage.removeItem(ADMIN_SESSION_KEY);
};

export const isAuthenticated = () => {
  return !!cachedAdminSession || !!localStorage.getItem(ADMIN_TOKEN_KEY);
};

export const getAdminSession = () => {
  if (cachedAdminSession) {
    return cachedAdminSession;
  }
  try {
    const raw = localStorage.getItem(ADMIN_SESSION_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (error) {
    return null;
  }
};

export const isSuperAdmin = () => getAdminSession()?.role === 'superadmin';

export const getAdminMe = async () => {
  const response = await adminClient.get('/me');
  cachedAdminSession = response.data?.user || null;
  return response.data;
};

export const getAdminUsers = async () => {
  const response = await adminClient.get('/users');
  return response.data;
};

export const createAdminUser = async (data) => {
  const response = await adminClient.post('/users', data);
  return response.data;
};

export const updateAdminUser = async (id, data) => {
  const response = await adminClient.put(`/users/${id}`, data);
  return response.data;
};

export const deleteAdminUser = async (id) => {
  const response = await adminClient.delete(`/users/${id}`);
  return response.data;
};

// Site Configs
export const getSiteConfigs = async () => {
  const response = await adminClient.get('/site-configs');
  return response.data;
};

export const createSiteConfig = async (data) => {
  const response = await adminClient.post('/site-configs', data);
  return response.data;
};

export const updateSiteConfig = async (siteId, data) => {
  const response = await adminClient.put(`/site-configs/${siteId}`, data);
  return response.data;
};

// Profile
export const updateProfile = async (data, siteId = null) => {
  const response = await adminClient.put('/profile', data, {
    headers: buildHeaders(siteId)
  });
  return response.data;
};

export const uploadResume = async (file, siteId = null) => {
  const formData = new FormData();
  formData.append('file', file);

  const response = await adminClient.post('/resume', formData, {
    headers: buildHeaders(siteId)
  });
  return response.data;
};

// Projects
export const createProject = async (data, siteId = null) => {
  const response = await adminClient.post('/projects', data, {
    headers: buildHeaders(siteId)
  });
  return response.data;
};

export const uploadProjectImage = async (id, file, siteId = null) => {
  const formData = new FormData();
  formData.append('file', file);

  const response = await adminClient.post(`/projects/${id}/image`, formData, {
    headers: buildHeaders(siteId)
  });
  return response.data;
};

export const updateProject = async (id, data, siteId = null) => {
  const response = await adminClient.put(`/projects/${id}`, data, {
    headers: buildHeaders(siteId)
  });
  return response.data;
};

export const deleteProject = async (id, siteId = null) => {
  const response = await adminClient.delete(`/projects/${id}`, {
    headers: buildHeaders(siteId)
  });
  return response.data;
};

// Skills
export const createSkill = async (data, siteId = null) => {
  const response = await adminClient.post('/skills', data, {
    headers: buildHeaders(siteId)
  });
  return response.data;
};

export const updateSkill = async (id, data, siteId = null) => {
  const response = await adminClient.put(`/skills/${id}`, data, {
    headers: buildHeaders(siteId)
  });
  return response.data;
};

export const deleteSkill = async (id, siteId = null) => {
  const response = await adminClient.delete(`/skills/${id}`, {
    headers: buildHeaders(siteId)
  });
  return response.data;
};

// Experience
export const createExperience = async (data, siteId = null) => {
  const response = await adminClient.post('/experience', data, {
    headers: buildHeaders(siteId)
  });
  return response.data;
};

export const updateExperience = async (id, data, siteId = null) => {
  const response = await adminClient.put(`/experience/${id}`, data, {
    headers: buildHeaders(siteId)
  });
  return response.data;
};

export const deleteExperience = async (id, siteId = null) => {
  const response = await adminClient.delete(`/experience/${id}`, {
    headers: buildHeaders(siteId)
  });
  return response.data;
};

// Education
export const createEducation = async (data, siteId = null) => {
  const response = await adminClient.post('/education', data, {
    headers: buildHeaders(siteId)
  });
  return response.data;
};

export const updateEducation = async (id, data, siteId = null) => {
  const response = await adminClient.put(`/education/${id}`, data, {
    headers: buildHeaders(siteId)
  });
  return response.data;
};

export const deleteEducation = async (id, siteId = null) => {
  const response = await adminClient.delete(`/education/${id}`, {
    headers: buildHeaders(siteId)
  });
  return response.data;
};

// Contacts
export const getContacts = async (siteId = null) => {
  const response = await adminClient.get('/contacts', {
    headers: buildHeaders(siteId)
  });
  return response.data;
};

export const markContactRead = async (id, siteId = null) => {
  const response = await adminClient.put(`/contacts/${id}/read`, {}, {
    headers: buildHeaders(siteId)
  });
  return response.data;
};

export const deleteContact = async (id, siteId = null) => {
  const response = await adminClient.delete(`/contacts/${id}`, {
    headers: buildHeaders(siteId)
  });
  return response.data;
};
